import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { getMessagesSettings, manageMessageSettings } from 'services/actions/messageService';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';

interface MessageSettingsProps {
  selectedUser: any;
}

const MessageSettings: React.FC<MessageSettingsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [fileName, setFileName] = useState<string>('');
  const [status, setStatus] = useState<string>('Activate');
  const [template, setTemplate] = useState<string>('');
  const [action_id, setActionID] = useState<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [categoryEnabled, setCategoryEnabled] = useState<boolean>(true);
  const [hoveredChip, setHoveredChip] = useState<string | null>(null);

  // Theme tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const sectionHeaderColor = isDark ? '#E2E8F0' : '#314158';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const switchOnBg = isDark ? '#51A2FF' : '#2B7FFF';
  const switchKnob = isDark ? '#0F172B' : '#FFFFFF';
  const switchOffBg = isDark ? '#314158' : '#E2E8F0';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const warningBg = isDark ? '#422006' : '#FFFBEB';
  const warningBorder = isDark ? '#92400E' : '#FDE68A';
  const warningText = isDark ? '#FCD34D' : '#92400E';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const disabledLabelColor = isDark ? '#62748E' : '#90A1B9';
  const variableChipBg = isDark ? '#1D293D' : '#F1F5F9';
  const variableChipText = isDark ? '#CAD5E2' : '#45556C';
  const variableChipHoverBg = isDark ? '#162456' : '#EFF6FF';
  const variableChipHoverText = isDark ? '#51A2FF' : '#2B7FFF';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response: any = await getMessagesSettings(selectedUser);
        if (response && response.length > 0) {
          const setting = response[0];
          setStatus(setting.is_active ? 'Activate' : 'Deactivate');
          setActionID(setting.action_id);
          if (setting.category_enabled === false) {
            setCategoryEnabled(false);
          }
          if (setting.settings_value?.length > 0) {
            setTemplate(setting.settings_value[0].template_data || '');
          }
        }
        setLoading(false);
      } catch (error: any) {
        console.error('Error fetching settings:', error.message);
        toast.error(t('messageSettings.errors.fetchFailed'));
        setLoading(false);
      }
    };

    if (selectedUser) {
      fetchSettings();
    }
  }, [selectedUser]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName('');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const toggleStatus = async () => {
    const newStatus = status === 'Activate' ? 'Deactivate' : 'Activate';
    const isActive = newStatus === 'Activate';

    try {
      setStatus(newStatus);
      await manageMessageSettings(
        selectedUser,
        isActive,
        template,
        action_id
      );
      toast.success(t('messageSettings.messages.statusUpdated', { status: newStatus }));
    } catch (error: any) {
      console.error('Error toggling status:', error.message);
      setStatus(status);
      toast.error(t('messageSettings.errors.statusUpdateFailed'));
    }
  };

  const handleSaveDraft = async () => {
    try {
      await manageMessageSettings(
        selectedUser,
        status === 'Activate',
        template,
        action_id
      );
      toast.success(t('messageSettings.messages.templateSaved'));
    } catch (error: any) {
      console.error('Error saving draft:', error.message);
      toast.error(t('messageSettings.errors.templateSaveFailed'));
    }
  };

  const handleVariableClick = (variable: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const cursorPos = textarea.selectionStart;
      const textBefore = template.substring(0, cursorPos);
      const textAfter = template.substring(cursorPos);
      const newTemplate = `${textBefore}${variable}${textAfter}`;
      setTemplate(newTemplate);
      // Move cursor to after the inserted variable
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = cursorPos + variable.length;
        textarea.focus();
      }, 0);
    }
  };

  if (loading) {
    return <Spinner size="large" text={t('messageSettings.loading')} fullPage />;
  }

  const isActive = status === 'Activate';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* General Settings Section */}
      <div>
        <div style={{ borderBottom: `1px solid ${sectionBorder}`, paddingBottom: '16px' }}>
          <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: sectionHeaderColor }}>
            {t('messageSettings.sections.generalSettings')}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          {!categoryEnabled && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '12px',
              background: warningBg,
              border: `1px solid ${warningBorder}`,
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 400, color: warningText }}>
                {t('messageSettings.warnings.categoryDisabled')}
              </span>
            </div>
          )}

          {/* Activation Toggle */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <span style={{
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 500,
                color: !categoryEnabled ? disabledLabelColor : labelColor
              }}>
                {t('messageSettings.labels.status')}
              </span>
              <span style={{
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 400,
                color: descriptionColor
              }}>
                {t('messageSettings.descriptions.activation', 'Turn on or off. When disabled, no messages will be sent.')}
              </span>
            </div>
            <div
              onClick={!categoryEnabled ? undefined : toggleStatus}
              style={{
                width: '40px',
                height: '20px',
                borderRadius: 999,
                background: isActive ? switchOnBg : switchOffBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isActive ? 'flex-end' : 'flex-start',
                padding: '2px',
                cursor: !categoryEnabled ? 'not-allowed' : 'pointer',
                opacity: !categoryEnabled ? 0.5 : 1,
                transition: 'background 0.2s ease',
                flexShrink: 0
              }}
            >
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: 999,
                background: switchKnob,
                transition: 'all 0.2s ease'
              }} />
            </div>
          </div>

        </div>
      </div>

      {/* Message Template Section */}
      <div>
        <div style={{ borderBottom: `1px solid ${sectionBorder}`, paddingBottom: '16px' }}>
          <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: sectionHeaderColor }}>
            {t('messageSettings.sections.messageTemplate')}
          </span>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginTop: '16px',
          padding: '20px',
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          opacity: !categoryEnabled ? 0.5 : 1
        }}>
          {/* Template Header */}
          <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: labelColor }}>
            {t('messageSettings.labels.definedTemplate')}
          </span>

          {/* Template Textarea */}
          <textarea
            ref={textareaRef}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              lineHeight: '20px',
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              background: inputBg,
              color: inputText,
              outline: 'none',
              resize: 'vertical',
              cursor: !categoryEnabled ? 'not-allowed' : 'text',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: '120px'
            }}
            placeholder={t('messageSettings.placeholders.writeMessage')}
            value={template}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTemplate(e.target.value)}
            rows={6}
            disabled={!categoryEnabled}
          />

          {/* Variables row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 400, color: descriptionColor }}>
              {t('messageSettings.hints.clickToInsert')}
            </span>
            {['{{firstname}}', '{{username}}', '{{full_name}}'].map((variable) => (
              <span
                key={variable}
                style={{
                  padding: '4px 10px',
                  background: hoveredChip === variable ? variableChipHoverBg : variableChipBg,
                  color: hoveredChip === variable ? variableChipHoverText : variableChipText,
                  borderRadius: '6px',
                  fontSize: '12px',
                  lineHeight: '16px',
                  fontFamily: 'monospace',
                  cursor: !categoryEnabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  border: `1px solid ${hoveredChip === variable ? (isDark ? '#1E3A8A' : '#BFDBFE') : 'transparent'}`
                }}
                onClick={() => categoryEnabled && handleVariableClick(variable)}
                onMouseEnter={() => setHoveredChip(variable)}
                onMouseLeave={() => setHoveredChip(null)}
              >
                {variable}
              </span>
            ))}
          </div>

          {/* Save button */}
          <button
            style={{
              alignSelf: 'flex-end',
              padding: '8px 24px',
              borderRadius: '8px',
              border: 'none',
              background: !categoryEnabled ? (isDark ? '#314158' : '#90A1B9') : buttonBg,
              color: '#FFFFFF',
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              cursor: !categoryEnabled ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onClick={handleSaveDraft}
            disabled={!categoryEnabled}
          >
            {t('messageSettings.buttons.saveTemplate')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageSettings;
